// src/styles/themes/theme.js
import { createTheme } from "@mui/material/styles";

const theme = createTheme({
  palette: {
    primary: {
      main: "#0d2445", // Deep navy
      light: "#2e4a6f", // Lightened by ~20%
      dark: "#081a31", // Darkened by ~20%
      50: "#b3c1d3", // Very light for backgrounds
      100: "#5b749a", // Lighter shade
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#d72329", // Vibrant red
      light: "#e04b50", // Lightened by ~10%
      dark: "#a61b20", // Darkened by ~20%
      50: "#f7b3b5", // Very light for backgrounds
      100: "#e97477", // Lighter shade
      contrastText: "#ffffff",
    },
    error: {
      main: "#dd2c00", // Accent color as error
      light: "#e55533", // Lightened by ~10%
      dark: "#a62200", // Darkened by ~20%
      50: "#f7b8a3", // Very light
      100: "#ee7e66", // Lighter shade
      contrastText: "#ffffff",
    },
    grey: {
      50: "#fafafa",
      100: "#e6e6e6", // Light gray
      200: "#d9d9d9",
      300: "#cacaca", // Medium gray
      400: "#b0b0b0",
      500: "#9e9e9e",
      600: "#8a8a8a", // Dark gray
      700: "#757575",
      800: "#616161",
      900: "#424242",
    },
    background: {
      default: "#edeff4", // Light gray for app background
      paper: "#ffffff", // White for cards, drawers
    },
    text: {
      primary: "#212121",
      secondary: "#757575",
      disabled: "#8a8a8a", // Dark gray for disabled text
    },
    warning: {
      main: "#f57c00",
      contrastText: "#ffffff",
    },
    info: {
      main: "#0288d1",
      contrastText: "#ffffff",
    },
    success: {
      main: "#388e3c",
      contrastText: "#ffffff",
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h6: {
      fontSize: "1.25rem",
      fontWeight: 500,
    },
    body1: {
      fontSize: "0.875rem",
      fontWeight: 400,
    },
    body2: {
      fontSize: "0.75rem",
      fontWeight: 400,
    },
    button: {
      fontSize: "0.875rem",
      fontWeight: 500,
      textTransform: "none",
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#ffffff",
          color: "#212121",
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: 48,
          "@media (min-width: 600px)": {
            minHeight: 56,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          padding: "6px 16px",
        },
        outlined: {
          borderColor: "#0d2445", // Primary
          color: "#0d2445",
          "&:hover": {
            backgroundColor: "#b3c1d3", // Primary[50]
            borderColor: "#0d2445",
          },
        },
        containedPrimary: {
          backgroundColor: "#0d2445", // Primary
          "&:hover": {
            backgroundColor: "#081a31", // Primary.dark
          },
        },
        containedSecondary: {
          backgroundColor: "#d72329", // Secondary
          "&:hover": {
            backgroundColor: "#a61b20", // Secondary.dark
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: "none",
          boxShadow: "2px 0 4px rgba(0, 0, 0, 0.1)",
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#424242", // Grey[900]
          color: "#ffffff",
          fontSize: "0.75rem",
          padding: "4px 8px",
        },
        arrow: {
          color: "#424242",
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          bottom: 72, // Account for BottomBar height
        },
      },
    },
  },
  shape: {
    borderRadius: 4,
  },
  spacing: 8,
});

export default theme;
